/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable camelcase */
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import { connectFavorites } from './Favorites/FavoritesConnectors';
import ShingleColorDescription from './ShingleColorDescription';
import { trackCompareCheckboxClick } from './analytics';
import { FeatureImage } from './feature_utils';
import { ENABLED_FEATURE_FLAGS } from './features';
import { altText } from './imageAlts';
import withDefaultPrevented from '../../../withDefaultPrevented';
import LazyImage from '../../LazyImage';
import { PrefixWithLocale } from '../../Utilities';
import { connectLocation } from '../../location/LocationConnectors';

const pink = '#D40F7D';
const heartOpen = `\f08a`;
const heartFilled = '\f004';

const shingleColor = css`
  display: inline-block;
  margin: 4px;
  padding: 0px 8px;
  text-decoration: none;
  width: 130px;
  cursor: pointer;
  position: relative;

  @media (min-width: 768px) {
    width: 150px;
    margin: 5px;
  }

  @media (min-width: 1200px) {
    width: 165px;
  }

  img {
    display: block;
    max-width: 100%;
    width: 100%;
    white-space: break-spaces;
    font-size: 14px;

    @media (min-width: 768px) {
      height: 150px;
    }

    @media (min-width: 1200px) {
      height: 165px;
    }
  }

  a {
    text-decoration: none;

    &:focus {
      outline: none;

      img {
        outline: 2px solid #d40f7d;
      }

      span {
        color: #d40f7d;
      }
    }
  }

  &.shingle-color--selected a img:not(.feature-flag) {
    outline: 5px solid #d40f7d;
    opacity: 1;
  }
`;

const colorName = css`
  display: block;
  font-size: 14px;
  font-weight: bold;
  line-height: 22px;
  margin-top: 4px;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const bottomSection = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  width: 100%;
`;

const compareSection = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const compareCheckbox = css`
  width: 16px;
  height: 16px;
  cursor: pointer;
  margin: 0;
  accent-color: #d40f7d;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const compareLabel = css`
  font-size: 14px;
  color: #333;
  cursor: pointer;
  user-select: none;
  font-weight: normal;
`;

const favoriteToggle = css`
  align-items: center;
  color: #d40f7d;
  display: flex;
  font-size: 20px;
  height: 32px;
  justify-content: center;
  width: 32px;
`;

const favoriteCheckbox = css`
  -moz-appearance: none;
  -webkit-appearance: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  background-color: transparent;
  border: none;
  cursor: pointer;
  height: 32px;
  margin: 0;
  outline: none;
  width: 32px;

  & + i {
    cursor: pointer;
    font-size: 20px;
    position: relative;
  }

  & + i:before {
    content: '${heartOpen}';
  }

  & + i:hover:before {
    @media (min-width: 1200px) {
      color: #d40f7d;
      content: '${heartFilled}';
    }
  }

  &:hover + i:before {
    @media (min-width: 1200px) {
      color: #d40f7d;
      content: '${heartFilled}';
    }
  }

  &:checked + i:before {
    content: '${heartFilled}';
    color: ${pink};
  }

  &:focus + i {
    border: 1px solid #333;
    padding: 3px;
  }
`;

const FavoriteIcon = styled.i`
  color: #d40f7d;
`;

const Color = ({
  shingle,
  color,
  favorites,
  removeFromFavorites,
  addToFavorites,
  houseView,
  onClick,
  isSelected,
  isPDP,
  t,
  address,
  locale,
  compared = [],
  addToCompared,
  removeFromCompared,
  canAddMore = true,
}) => {
  const { uid, name, house_cropped_768x768_url2: houseImage, swatch_160x160_url: image, description } = color;
  const favorite = favorites.includes(color.uuid);
  const isCompared = compared.includes(color.uuid);
  const onToggleFavorite = () => (favorite ? removeFromFavorites(color.uuid) : addToFavorites(color.uuid));
  const onToggleCompare = () => {
    if (isCompared) {
      removeFromCompared?.(color.uuid);
    } else {
      // Track analytics when adding to comparison
      trackCompareCheckboxClick(shingle, color);
      addToCompared?.(color.uuid);
    }
  };
  const isCheckboxDisabled = !canAddMore && !isCompared;
  const i18n_shingles = t('shingles.shingles').toLowerCase();

  return (
    <li css={shingleColor} className={`shingle-color ${isSelected && 'shingle-color--selected'}`}>
      <a
        href={PrefixWithLocale(`/roofing/shingles/${shingle.uid}?shingle-color=${uid}`, locale.code)}
        data-track="view-product"
        data-track-product-name={shingle.proper_name}
        data-track-product-color={name}
        tabIndex="0"
        aria-label={`View ${name} Color`}
        onClick={onClick && withDefaultPrevented(() => onClick(color))}
      >
        <FeatureImage address={address} item={color} className="filters" featuresSubset={ENABLED_FEATURE_FLAGS} />
        <LazyImage
          src={image}
          alt={altText(
            color,
            'swatch',
            locale.code,
            houseView ? '' : `${shingle.name} ${description || name} ${i18n_shingles}`
          )}
          display={houseView ? 'none' : 'block'}
        />
        <LazyImage
          src={houseImage}
          alt={altText(
            color,
            'house_thumb',
            locale.code,
            houseView ? `${shingle.name} ${description || name} ${t('shingles.two_story_home')}` : ''
          )}
          display={houseView ? 'block' : 'none'}
        />

        <span aria-hidden="true" css={colorName}>
          {name}
        </span>
        {isPDP && <ShingleColorDescription description={description} className="tablet-desktop-only" />}
      </a>

      <div css={bottomSection}>
        <div
          css={[
            compareSection,
            isCheckboxDisabled &&
              css`
                opacity: 0.5;
                pointer-events: none;
              `,
          ]}
        >
          <input
            type="checkbox"
            css={compareCheckbox}
            checked={isCompared}
            onChange={onToggleCompare}
            disabled={isCheckboxDisabled}
            id={`compare-${color.uuid}`}
          />
          <label css={compareLabel} htmlFor={`compare-${color.uuid}`}>
            Compare
          </label>
        </div>

        <label css={favoriteToggle} className="favorite-toggle">
          <input
            type="checkbox"
            css={favoriteCheckbox}
            checked={favorite}
            onChange={onToggleFavorite}
            data-track={favorite ? 'remove-favorite' : 'make-favorite'}
            data-track-product-name={shingle.uid}
            data-track-product-color={color.name}
          />
          <FavoriteIcon className={favorite ? 'fa fa-heart' : 'fa fa-heart-o'} checked={favorite} aria-hidden="true" />
          <span className="sr-only">
            {favorite ? 'Remove' : 'Add'} {color.name} {isPDP ? '' : `${shingle.proper_name} ${i18n_shingles}`}{' '}
            {favorite ? 'from' : 'to'} ${t('shingles.favorites')}
          </span>
        </label>
      </div>
    </li>
  );
};

Color.propTypes = {
  shingle: PropTypes.shape({
    uid: PropTypes.string,
    proper_name: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  color: PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
    uuid: PropTypes.string,
    house_cropped_768x768_url2: PropTypes.string,
    swatch_160x160_url: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  favorites: PropTypes.arrayOf(PropTypes.string),
  removeFromFavorites: PropTypes.func,
  addToFavorites: PropTypes.func,
  houseView: PropTypes.bool,
  onClick: PropTypes.func,
  isSelected: PropTypes.bool,
  isPDP: PropTypes.bool,
  t: PropTypes.func,
  address: PropTypes.shape({}),
  locale: PropTypes.shape({
    code: PropTypes.string,
  }),
  compared: PropTypes.arrayOf(PropTypes.string),
  addToCompared: PropTypes.func,
  removeFromCompared: PropTypes.func,
  canAddMore: PropTypes.bool,
};

Color.defaultProps = {
  favorites: [],
  removeFromFavorites: null,
  addToFavorites: null,
  houseView: false,
  onClick: null,
  isSelected: false,
  isPDP: false,
  t: key => key,
  address: null,
  locale: { code: 'en-us' },
  compared: [],
  addToCompared: null,
  removeFromCompared: null,
  canAddMore: true,
};

export default connectLocation(connectFavorites(Color));
